import React from 'react';
import PropTypes from 'prop-types';

import {ArrowRightIcon} from '@amblique/king-living/app/components/icons';
import {Box, Flex, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import {ensureArray} from '@amblique/king-living/app/utils/utils';

import {useMenuItemById} from '@amblique/contentful/app/contentful/hooks/use-menu-item';
import {Link as ContentfulLink} from '@amblique/king-living/app/contentful/models/link';
import {Image as ContentfulImage} from '@amblique/king-living/app/contentful/models/image';
import {NavigationColumn as ContentfulNavigationColumn} from '@amblique/king-living/app/contentful/models/navigation-column';

const isIncompleteMenuItem = (menuItem) => {
  if (!menuItem?.sys?.id || !menuItem.title) {
    return false;
  }

  const hasSubmenuField = 'submenuCollection' in menuItem;
  const hasLinkField = 'link' in menuItem;

  return !hasLinkField && !hasSubmenuField;
};

const hasSubmenu = (menuItem) => {
  return ensureArray(menuItem?.submenuCollection?.items).filter(Boolean).length > 0;
};

const isNavigationColumnItem = (subItem) =>
  Boolean(subItem && (subItem.__typename === 'NavigationColumn' || subItem.linksCollection));

/** Total visual columns in the desktop mega-menu grid. */
export const MEGA_MENU_TOTAL_COLUMNS = 5;

/**
 * How many outer-grid columns the nav wrapper should span.
 * Each image reserves 1 column; nav gets the remainder.
 *
 * @param {number} imageCount
 * @param {number} [totalColumns]
 * @returns {number}
 */
export const getMegaMenuNavColumnSpan = (
  imageCount,
  totalColumns = MEGA_MENU_TOTAL_COLUMNS
) => {
  const cappedImages = Math.min(Math.max(0, imageCount || 0), totalColumns);

  return Math.max(0, totalColumns - cappedImages);
};

// Nav shop-all links — plain URL, not CMS button variants.
export const toShopAllLink = (link) => {
  if (!link) {
    return null;
  }

  return {
    ...link,
    linkType: undefined,
    type: undefined,
  };
};

export const getLinkDisplayText = (link) => link?.linkText ?? link?.text;

// Render for desktop — single CSS Grid: [nav-wrapper | image × N]
const renderForDesktop = (hasImages, images, menuItem, submenuItems, styles, props) => {
  const safeSubmenuItems = ensureArray(submenuItems).filter(Boolean);
  const safeImages = ensureArray(images).filter(Boolean);
  const shopAllLink = menuItem?.featuredLink || menuItem?.link;
  const navColumns = safeSubmenuItems.filter(isNavigationColumnItem);
  const navColumnSpan = getMegaMenuNavColumnSpan(safeImages.length);
  const showNavWrapper = navColumns.length > 0 && navColumnSpan > 0;

  return (
    <Box
      className="menu-item-container menu-item-container__desktop"
      {...styles.menuItemDesktopContainer}
      {...props}
    >
      <Box className="menu-item-desktop-grid" {...styles.menuItemDesktopGrid}>
        {showNavWrapper && (
          <Box
            className="nav-wrapper"
            {...styles.navWrapper}
            gridColumn={`span ${navColumnSpan}`}
            gridTemplateColumns={`repeat(${navColumnSpan}, minmax(0, 1fr))`}
          >
            {navColumns.map((subItem) => (
              <ContentfulNavigationColumn key={subItem?.sys?.id} component={subItem} />
            ))}
          </Box>
        )}

        {hasImages &&
          safeImages.map((image) => (
            <Box
              key={image?.sys?.id}
              className="menu-item-image menu-item-image--desktop"
              {...styles.menuItemDesktopImage}
            >
              <ContentfulImage component={image} />
            </Box>
          ))}
      </Box>
      {shopAllLink && (
        <Flex alignItems="center"
          className="shop-all-link-wrapper"
          flexDirection="row"
          flexWrap="wrap"
          gap={2}
          {...styles.shopAllLinkWrapper}
        >
          <ContentfulLink className="shop-all-link" component={toShopAllLink(shopAllLink)}>
            {getLinkDisplayText(shopAllLink)} <ArrowRightIcon />
          </ContentfulLink>
        </Flex>
      )}
    </Box>
  );
};

// Render for mobile
const renderForMobile = (hasImages, hasSubmenuItems, handleClick, link, submenuItems, images, styles, props) => {
  const safeSubmenuItems = ensureArray(submenuItems).filter(Boolean);
  const safeImages = ensureArray(images).filter(Boolean);

  return (
    <Box className="menu-item-renderer menu-item-container__mobile" {...props}>
      <Box className="submenu-items-mobile-wrapper" {...styles.mobileSubmenuItemsWrapper}>
        {safeSubmenuItems.length > 0 && safeSubmenuItems.map((subItem) => (
          <Box key={subItem?.sys?.id} className="submenu-item-mobile" {...styles.menuItemContainer} {...styles.mobileSubmenuItem}>
            <ContentfulNavigationColumn
              component={subItem}
            />
          </Box>
        ))}

        {hasImages && (
          <Box className="menu-item-images menu-item-images__mobile" {...styles.menuItemImages}>
            {safeImages.map((image) => (
              <ContentfulImage key={image?.sys?.id} className="menu-item-image" component={image} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const MenuItemContent = ({
  menuItem,
  onItemClick,
  isMobile = false,
  submenuItems = [],
  ...props
}) => {
  const menuItemId = menuItem?.sys?.id;
  const needsFullData = isIncompleteMenuItem(menuItem);

  const {data: fullMenuItemData} = useMenuItemById(menuItemId, {
    variant: 'default',
    enabled: needsFullData && !!menuItemId,
  });

  // Use full data if available, otherwise use passed menuItem
  const resolvedMenuItem = fullMenuItemData || menuItem;
  const hasSubmenuItems = hasSubmenu(resolvedMenuItem);

  const handleClick = (e) => {
    if (hasSubmenuItems && onItemClick) {
      e.preventDefault();
      onItemClick(resolvedMenuItem);
    }
  };

  const styles = useMultiStyleConfig('MegaMenu', {isMobile});

  const link = resolvedMenuItem?.link || null;
  const imagesCollection = resolvedMenuItem?.imagesCollection;
  const images = ensureArray(imagesCollection?.items).filter(Boolean);
  const hasImages = images.length > 0;
  const resolvedSubmenuItems = ensureArray(resolvedMenuItem?.submenuCollection?.items).filter(Boolean);

  return (
    <Box className="menu-item-content" {...props}>
      {isMobile ? renderForMobile(hasImages, hasSubmenuItems, handleClick, link, resolvedSubmenuItems, images, styles, props) :
        renderForDesktop(hasImages, images, resolvedMenuItem, resolvedSubmenuItems, styles, props)}
    </Box>
  );
};

MenuItemContent.displayName = 'MenuItemContent';
MenuItemContent.propTypes = {
  isMobile: PropTypes.bool,
  menuItem: PropTypes.object.isRequired,
  onItemClick: PropTypes.func,
  submenuItems: PropTypes.array,
};
